import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { globalQueryKeys } from '@opallabs/api'
import { stamps } from '@opallabs/api/v2'

interface GetStampsParams {
  /** The numeric workspace id — `/stamps/v2` cannot resolve a uuid. */
  workspaceId: string
  /** Set false to hold the paging back until the list is wanted. */
  enabled?: boolean
  stampType?: 'moment' | 'post'
  /**
   * The numeric channel id. Post stamps only build content for the channel
   * their own content is on, so a picker for one channel asks for those.
   */
  serviceId?: string
  useErrorBoundary?: boolean
}

const LIMIT = 150

const toFilter = ({ serviceId, stampType }: GetStampsParams) => ({
  ...(stampType === undefined ? {} : { stamp_type: stampType }),
  ...(serviceId === undefined ? {} : { service_id: Number(serviceId) }),
})

const fetchStamps = (params: GetStampsParams, page: number) =>
  stamps.read({
    params: {
      query: {
        brand_id: params.workspaceId,
        filter: toFilter(params),
        page: { offset: page * LIMIT, limit: LIMIT },
      },
    },
  })

export const useWorkspaceStamps = ({
  workspaceId,
  enabled = true,
  stampType,
  serviceId,
  useErrorBoundary,
}: GetStampsParams) => {
  const params = { serviceId, stampType, workspaceId }

  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    enabled,
    refetchInterval: false,
    useErrorBoundary,
    queryKey: globalQueryKeys.workspaceStamps(workspaceId, {
      filter: toFilter(params),
    }),
    queryFn: ({ pageParam }) => fetchStamps(params, pageParam ?? 0),
    getNextPageParam: (_lastPage, pages) => {
      if (!pages[0].meta) return undefined
      const nextOffset = pages.length * LIMIT
      return pages[0].meta.total > nextOffset ? pages.length : undefined
    },
  })

  // `meta.total` counts what the filters return, so this walks to the end of
  // the filtered set rather than the first page of it. `enabled` stops the
  // query starting, but not this effect: a disabled query still reports the
  // pages another caller left in the cache, and asking for the next one would
  // fetch it.
  useEffect(() => {
    if (!enabled || !data || !hasNextPage || isFetching || isFetchingNextPage)
      return
    fetchNextPage()
  }, [
    data,
    enabled,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  ])

  const stampList = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((page) => page.data)
        .map((stamp) => ({
          name: stamp.attributes.name,
          uuid: stamp.attributes.uuid,
          permissionMode: stamp.attributes.permission_mode,
        })),
    [data]
  )

  return {
    stamps: stampList,
    fetchingComplete: !hasNextPage && !isFetching,
    refetch,
  }
}
